import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Loader2, Search, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { massnahmenAPI, masterMeasuresAPI } from '../../lib/api';

const fmtDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return d && m && y ? `${d}.${m}.${y}` : iso;
};

const foerderLabel = (v) => {
  if (v.foerder_start && v.foerder_end) return `Förderzeitraum: ${fmtDate(v.foerder_start)} – ${fmtDate(v.foerder_end)}`;
  if (v.foerder_start) return `Förderzeitraum: ab ${fmtDate(v.foerder_start)}`;
  if (v.foerder_end) return `Förderzeitraum: bis ${fmtDate(v.foerder_end)}`;
  return '';
};

export const AssignVergabenDialog = ({ massnahme, onClose, onSaved }) => {
  const open = !!massnahme;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [vergaben, setVergaben] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!massnahme) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setSearch('');
        const res = await masterMeasuresAPI.getAll();
        if (cancelled) return;
        setVergaben(res.data || []);
        setSelected(new Set(massnahme.parent_measure_ids || []));
      } catch {
        if (!cancelled) toast.error('Vergaben konnten nicht geladen werden');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [massnahme]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return vergaben
      .filter((v) => {
        if (!q) return true;
        return [v.name, v.vergabenummer, v.los_nr, v.auftraggeber_name]
          .some((f) => (f || '').toString().toLowerCase().includes(q));
      })
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [vergaben, search]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const filteredIds = filtered.map((v) => v.id);
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));
  const toggleAllFiltered = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) filteredIds.forEach((id) => next.delete(id));
      else filteredIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const save = async () => {
    try {
      setSaving(true);
      await massnahmenAPI.update(massnahme.id, { parent_measure_ids: Array.from(selected) });
      toast.success(`${selected.size} Vergabe(n) zugeordnet`);
      onSaved?.();
      onClose();
    } catch {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90dvh] overflow-hidden flex flex-col" data-testid="assign-vergaben-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5" />
            <span>Vergaben zuordnen{massnahme?.name ? ` — ${massnahme.name}` : ''}</span>
          </DialogTitle>
          <DialogDescription>
            Übergeordnete Maßnahmen an- oder abwählen, denen diese
            operative Maßnahme zugeordnet ist. Speichern setzt die Zuordnung in einem Schritt.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suche Name / Vergabe-Nr. / Los / Auftraggeber"
            className="pl-8"
            data-testid="assign-vergaben-search"
          />
        </div>

        <div className="flex items-center justify-between text-sm">
          <Button
            variant="outline"
            size="sm"
            onClick={toggleAllFiltered}
            disabled={filteredIds.length === 0}
            className="rounded-full"
            data-testid="assign-vergaben-toggle-all"
          >
            {allFilteredSelected ? 'Keine (gefiltert)' : 'Alle (gefiltert)'}
          </Button>
          <span className="text-muted-foreground" data-testid="assign-vergaben-count">
            {selected.size} zugeordnet
          </span>
        </div>

        <div className="flex-1 overflow-y-auto rounded-lg border divide-y min-h-[200px]" data-testid="assign-vergaben-list">
          {loading ? (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-10">
              <Loader2 className="h-4 w-4 animate-spin" /> Wird geladen …
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10">Keine Vergabe gefunden.</p>
          ) : (
            filtered.map((v) => (
              <label
                key={v.id}
                className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-secondary/30"
                data-testid={`assign-vergabe-item-${v.id}`}
              >
                <Checkbox
                  checked={selected.has(v.id)}
                  onCheckedChange={() => toggle(v.id)}
                  data-testid={`assign-vergabe-checkbox-${v.id}`}
                />
                <span className="flex-1 min-w-0">
                  <span className="font-medium">{v.name}</span>
                  {!v.is_active && <Badge variant="secondary" className="ml-2 text-xs">inaktiv</Badge>}
                  <span className="block text-xs text-muted-foreground truncate">
                    {[
                      v.vergabenummer && `Vergabe: ${v.vergabenummer}`,
                      v.los_nr && `Los: ${v.los_nr}`,
                      v.auftraggeber_name,
                    ].filter(Boolean).join(' · ')}
                  </span>
                  {foerderLabel(v) && (
                    <span className="block text-xs text-muted-foreground" data-testid={`assign-vergabe-foerder-${v.id}`}>
                      {foerderLabel(v)}
                    </span>
                  )}
                </span>
              </label>
            ))
          )}
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-full w-full sm:w-auto">Abbrechen</Button>
          <Button onClick={save} disabled={saving || loading} className="rounded-full w-full sm:w-auto" data-testid="assign-vergaben-save-btn">
            {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Speichern …</> : 'Zuordnung speichern'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
